"use client";
import posthog from "posthog-js";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Image from "next/image";
// Error boundaries must be Client Components

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    posthog.captureException(error);
  }, [error]);

  return (
    // global-error must include html and body tags
    <html>
      <body className="min-h-screen bg-background">
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background">
          <div className="container max-w-md mx-auto px-4">
            <Card className="border-border/50 shadow-2xl bg-card/95 backdrop-blur-sm">
              <CardHeader className="text-center space-y-4 pb-8">
                {/* Logo */}
                <div className="flex justify-center mb-6">
                  <div className="h-16 w-auto">
                    <Image
                      src="/caroffshore-logo.png"
                      alt="car off shore logo"
                      width={128}
                      height={128}
                      className="h-16 text-foreground"
                    />
                  </div>
                </div>

                {/* Error Icon */}
                <div className="mx-auto w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-10 h-10 text-destructive"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                </div>

                <CardTitle className="text-2xl font-playfair text-foreground">
                  Something went wrong
                </CardTitle>

                <CardDescription className="text-muted-foreground text-base leading-relaxed">
                  We encountered an unexpected error while processing your
                  request. Our team has been notified and is working to resolve
                  the issue.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Error Details (Development only) */}
                {process.env.NODE_ENV === "development" && (
                  <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                    <h4 className="text-sm font-semibold text-destructive mb-2">
                      Error Details (Development)
                    </h4>
                    <pre className="text-xs text-destructive/80 overflow-auto max-h-32">
                      {error.message}
                      {error.stack && `\n\n${error.stack}`}
                    </pre>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={reset}
                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => (window.location.href = "/")}
                    className="flex-1"
                  >
                    Go Home
                  </Button>
                </div>

                {/* Help Text */}
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    If this problem persists, please{" "}
                    <a
                      href="mailto:info@studio3dx.com"
                      className="text-primary hover:underline"
                    >
                      contact our support team
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Decorative Elements */}
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
